import ActionPlanBadge from '@/app/components/action-plan/components/common/action-plan-badge';
import ModalInterventionDetails from '@/app/components/action-plan/components/modals/details/modal-intervention-details';
import { Intervention } from '@/app/components/action-plan/types';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { formatCancerAndOthersNameText, formatCancerAndOthersRiskNameText, formatRiskDecimal } from '@/utils';
import { Box, Button, Flex, IconCheck, Text } from '@cmpsr/components';
import useActiveInterventionCardController from './active-intervention-card.controller';

type ActiveInterventionCardProps = {
  intervention: Intervention;
  onCompleteIntervention?: (intervention: Intervention) => void;
};

export default function ActiveInterventionCard({ intervention, onCompleteIntervention }: ActiveInterventionCardProps) {
  const { cancersAffected, isDetailsModalOpen, onCloseDetailsModal, onOpenDetailsModal, otherCancers, reducedRisk } =
    useActiveInterventionCardController({ intervention });
  const { title, category, isNonQuantifiable } = intervention;

  return (
    <Box
      as="section"
      backgroundColor="background-light"
      border="1px solid"
      borderColor="ui-element-divider"
      borderRadius="radii-3"
    >
      <Flex alignItems="center" justifyContent="space-between" mb={4}>
        <IconCatch
          size="xl"
          icon={category}
          position="relative"
          top="-1px"
          left="-1px"
          backgroundColor="tan"
          border="1px solid"
          borderColor="cream"
          borderRadius="radii-3"
          borderTopRightRadius={0}
          borderBottomLeftRadius={0}
          alt=""
        />

        <Box px={3} py={1} backgroundColor="tan" borderRadius="radii-1" mr={{ base: 5, lg: 6 }}>
          <Text variant="text-body-floating-label-bold" color="text-link-primary-default">
            {formatCancerAndOthersNameText({ reducedRisk, isNonQuantifiable, otherCancers })}
          </Text>
        </Box>
      </Flex>

      <Box px={{ base: 5, lg: 6 }} mb={4}>
        <Text as="h2" variant="text-header-L" pb="spacer-1">
          {title}
        </Text>
        <Flex alignItems="center" justifyContent="flex-start" gap={2}>
          <ActionPlanBadge text={formatRiskDecimal(reducedRisk.impact)} isNonQuantifiable={isNonQuantifiable} />
          <Text variant="text-body-meta-regular" color="text-link-primary-default">
            {formatCancerAndOthersRiskNameText({ reducedRisk, cancersAffected, otherCancers })}
          </Text>
        </Flex>
      </Box>

      <Flex pt={3} px={{ base: 5, lg: 6 }} pb={5} gap={{ base: 2, lg: 3 }}>
        <Button variant="primary-alt" size="s" w="full" border="2px solid" onClick={onOpenDetailsModal}>
          Details
        </Button>
        <Button
          size="s"
          width="full"
          leadingIcon={<IconCheck />}
          onClick={() => {
            if (onCompleteIntervention) onCompleteIntervention(intervention);
          }}
          variant="primary"
        >
          Mark as Complete
        </Button>
      </Flex>
      <ModalInterventionDetails isOpen={isDetailsModalOpen} onClose={onCloseDetailsModal} intervention={intervention} />
    </Box>
  );
}
